import React, { useState } from 'react';
import { Search, Plus, Edit, Trash2, RefreshCw, Users, Phone, Mail, GraduationCap, Upload, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import * as XLSX from 'xlsx';

interface FairDataEntry {
  id: string;
  fair_name: string;
  student_name: string;
  school?: string;
  desired_program?: string;
  english_level?: string;
  student_phone?: string;
  student_email?: string;
  guardian_name?: string;
  guardian_phone?: string;
  guardian_email?: string;
  notes?: string;
  status: string;
  branch_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  creator?: {
    name: string;
  };
  branch?: {
    name: string;
  };
}

const FairData: React.FC = () => {
  const { user } = useAuth();
  const [fairDataList, setFairDataList] = useState<FairDataEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [guardianSearchTerm, setGuardianSearchTerm] = useState('');
  const [fairFilter, setFairFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<FairDataEntry | null>(null);
  const [userBranchId, setUserBranchId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    fair_name: '',
    student_name: '',
    school: '',
    desired_program: '',
    english_level: '',
    student_phone: '',
    student_email: '',
    guardian_name: '',
    guardian_phone: '',
    guardian_email: '',
    notes: '',
    status: 'Yeni',
  });

  React.useEffect(() => {
    fetchUserBranch();
    fetchFairData();
  }, []);

  const fetchUserBranch = async () => {
    if (!user?.id) return;

    try {
      const { data: unitEmployee } = await supabase
        .from('unit_employees')
        .select(`
          units (
            branch_id
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      const branchId = unitEmployee?.units?.branch_id || null;
      setUserBranchId(branchId);
    } catch (err) {
      console.error('Şube bilgisi alınamadı:', err);
    }
  };

  const fetchFairData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('fair_data')
        .select(`
          *,
          creator:profiles!fair_data_created_by_fkey(name),
          branch:branches(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setFairDataList(data || []);
    } catch (error) {
      console.error('Fuar dataları yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.fair_name) {
      alert('Lütfen fuar adı giriniz!');
      return;
    }

    if (!formData.student_name) {
      alert('Lütfen öğrenci adı soyadı giriniz!');
      return;
    }

    if (!userBranchId) {
      alert('Şube bilgisi bulunamadı!');
      return;
    }

    try {
      const payload = {
        ...formData,
        branch_id: userBranchId,
        created_by: user?.id,
        updated_at: new Date().toISOString(),
      };

      if (showEditModal && selectedEntry) {
        const { error } = await supabase
          .from('fair_data')
          .update(payload)
          .eq('id', selectedEntry.id);

        if (error) throw error;
        alert('Kayıt güncellendi!');
      } else {
        const { error } = await supabase
          .from('fair_data')
          .insert([payload]);

        if (error) throw error;
        alert('Kayıt eklendi!');
      }

      closeModals();
      fetchFairData();
    } catch (error: any) {
      console.error('Kayıt kaydedilirken hata:', error);
      alert('Kayıt kaydedilirken hata oluştu: ' + error.message);
    }
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;

    try {
      const { error } = await supabase
        .from('fair_data')
        .delete()
        .eq('id', selectedEntry.id);

      if (error) throw error;

      alert('Kayıt silindi!');
      setShowDeleteConfirm(false);
      setSelectedEntry(null);
      fetchFairData();
    } catch (error: any) {
      console.error('Kayıt silinirken hata:', error);
      alert('Kayıt silinirken hata oluştu: ' + error.message);
    }
  };

  const openEditModal = (entry: FairDataEntry) => {
    setSelectedEntry(entry);
    setFormData({
      fair_name: entry.fair_name,
      student_name: entry.student_name,
      school: entry.school || '',
      desired_program: entry.desired_program || '',
      english_level: entry.english_level || '',
      student_phone: entry.student_phone || '',
      student_email: entry.student_email || '',
      guardian_name: entry.guardian_name || '',
      guardian_phone: entry.guardian_phone || '',
      guardian_email: entry.guardian_email || '',
      notes: entry.notes || '',
      status: entry.status || 'Yeni',
    });
    setShowEditModal(true);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedEntry(null);
    setFormData({
      fair_name: '',
      student_name: '',
      school: '',
      desired_program: '',
      english_level: '',
      student_phone: '',
      student_email: '',
      guardian_name: '',
      guardian_phone: '',
      guardian_email: '',
      notes: '',
      status: 'Yeni',
    });
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userBranchId) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const fairName = prompt('Bu kayıtlar hangi fuara ait? (Örn: İstanbul Eğitim Fuarı 2024)');
      if (!fairName) {
        alert('Fuar adı girilmedi, içe aktarma iptal edildi.');
        e.target.value = '';
        return;
      }

      const entries = jsonData.map((row: any) => ({
        fair_name: fairName,
        student_name: row['ÖĞRENCİ İSİM SOYİSİM'] || row['student_name'] || '',
        school: row['OKULU'] || row['school'] || '',
        desired_program: row['EĞİTİM ALMAK İSTEDİĞİ BÖLÜM'] || row['desired_program'] || '',
        english_level: row['İNGİLİZCE SEVİYESİ'] || row['english_level'] || '',
        student_phone: row['ÖĞRENCİ TEL'] || row['student_phone'] || '',
        student_email: row['ÖĞRENCİ MAİL'] || row['student_email'] || '',
        guardian_name: row['VELİ İSİM SOYİSİM'] || row['guardian_name'] || '',
        guardian_phone: row['VELİ TEL'] || row['guardian_phone'] || '',
        guardian_email: row['VELİ MAİL'] || row['guardian_email'] || '',
        notes: row['NOT'] || row['notes'] || '',
        status: row['Durumu'] || row['status'] || 'Yeni',
        branch_id: userBranchId,
        created_by: user?.id,
      }));

      const { error } = await supabase
        .from('fair_data')
        .insert(entries);

      if (error) throw error;

      alert(`${entries.length} kayıt başarıyla içe aktarıldı!`);
      fetchFairData();
    } catch (error: any) {
      console.error('İçe aktarma hatası:', error);
      alert('İçe aktarma hatası: ' + error.message);
    }

    e.target.value = '';
  };

  const handleExport = () => {
    const exportData = filteredEntries.map(entry => ({
      'FUAR ADI': entry.fair_name,
      'ŞUBE': entry.branch?.name || '',
      'ÖĞRENCİ İSİM SOYİSİM': entry.student_name,
      'OKULU': entry.school || '',
      'EĞİTİM ALMAK İSTEDİĞİ BÖLÜM': entry.desired_program || '',
      'İNGİLİZCE SEVİYESİ': entry.english_level || '',
      'ÖĞRENCİ TEL': entry.student_phone || '',
      'ÖĞRENCİ MAİL': entry.student_email || '',
      'VELİ İSİM SOYİSİM': entry.guardian_name || '',
      'VELİ TEL': entry.guardian_phone || '',
      'VELİ MAİL': entry.guardian_email || '',
      'NOT': entry.notes || '',
      'Durumu': entry.status,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fuar Dataları');
    XLSX.writeFile(wb, `fuar_datalari_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
  };

  const filteredEntries = fairDataList.filter(entry => {
    const matchesSearch =
      entry.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.school?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.student_phone?.includes(searchTerm) ||
      entry.student_email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesGuardianSearch = guardianSearchTerm === '' ||
      entry.guardian_name?.toLowerCase().includes(guardianSearchTerm.toLowerCase()) ||
      entry.guardian_phone?.includes(guardianSearchTerm) ||
      entry.guardian_email?.toLowerCase().includes(guardianSearchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || entry.status === statusFilter;
    const matchesFair = fairFilter === 'all' || entry.fair_name === fairFilter;

    return matchesSearch && matchesGuardianSearch && matchesStatus && matchesFair;
  });

  const uniqueFairNames = Array.from(new Set(fairDataList.map(entry => entry.fair_name))).sort();

  const stats = {
    total: fairDataList.length,
    selectedFair: filteredEntries.length,
    totalFairs: uniqueFairNames.length,
    converted: fairDataList.filter(e => e.status === 'Lead Oldu').length,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Yeni': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'Arandı': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'Görüşme Yapıldı': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'Lead Oldu': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'İlgilenmedi': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Fuar Dataları</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Toplam {filteredEntries.length} kayıt
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchFairData}
            disabled={loading}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Yenile</span>
          </button>
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Upload className="w-4 h-4" />
            <span>Dışa Aktar</span>
          </button>
          <label className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer">
            <Upload className="w-4 h-4" />
            <span>İçe Aktar</span>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImport}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Yeni Kayıt</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Kayıt</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <GraduationCap className="w-8 h-8 text-cyan-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.selectedFair}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Seçili Fuarda</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Calendar className="w-8 h-8 text-orange-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalFairs}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Fuar Sayısı</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Mail className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.converted}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Lead Oldu</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Öğrenci ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Veli ara..."
              value={guardianSearchTerm}
              onChange={(e) => setGuardianSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={fairFilter}
            onChange={(e) => setFairFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Fuarlar</option>
            {uniqueFairNames.map(fairName => (
              <option key={fairName} value={fairName}>{fairName}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="Yeni">Yeni</option>
            <option value="Arandı">Arandı</option>
            <option value="Görüşme Yapıldı">Görüşme Yapıldı</option>
            <option value="Lead Oldu">Lead Oldu</option>
            <option value="İlgilenmedi">İlgilenmedi</option>
          </select>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Kayıtlar yükleniyor...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Fuar Adı</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Şube</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Öğrenci İsim Soyisim</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Okulu</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Eğitim Almak İstediği Bölüm</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İngilizce Seviyesi</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Öğrenci Tel</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Öğrenci Mail</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Veli İsim Soyisim</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Veli Tel</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Veli Mail</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Not</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Durumu</th>
                  <th className="text-right py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <td className="py-4 px-6">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">{entry.fair_name}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{entry.branch?.name || '-'}</span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-base font-bold text-gray-900 dark:text-white">{entry.student_name}</p>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{entry.school || '-'}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{entry.desired_program || '-'}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{entry.english_level || '-'}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{entry.student_phone || '-'}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[150px] block">{entry.student_email || '-'}</span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-base font-bold text-gray-900 dark:text-white">{entry.guardian_name || '-'}</p>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{entry.guardian_phone || '-'}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[150px] block">{entry.guardian_email || '-'}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[150px] block">{entry.notes || '-'}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(entry.status)}`}>
                        {entry.status}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openEditModal(entry)}
                          className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Düzenle"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedEntry(entry);
                            setShowDeleteConfirm(true);
                          }}
                          className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && filteredEntries.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {searchTerm ? 'Kayıt bulunamadı' : 'Henüz kayıt yok'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchTerm
              ? 'Arama kriterlerinizi değiştirmeyi deneyin'
              : 'İlk kaydınızı ekleyerek veya Excel dosyası ile içe aktararak başlayın'
            }
          </p>
        </div>
      )}

      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                {showEditModal ? 'Kaydı Düzenle' : 'Yeni Kayıt Ekle'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fuar Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.fair_name}
                    onChange={(e) => setFormData({ ...formData, fair_name: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Örn: İstanbul Eğitim Fuarı 2024"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Öğrenci İsim Soyisim *
                    </label>
                    <input
                      type="text"
                      value={formData.student_name}
                      onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Okulu
                    </label>
                    <input
                      type="text"
                      value={formData.school}
                      onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Eğitim Almak İstediği Bölüm
                    </label>
                    <input
                      type="text"
                      value={formData.desired_program}
                      onChange={(e) => setFormData({ ...formData, desired_program: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      İngilizce Seviyesi
                    </label>
                    <select
                      value={formData.english_level}
                      onChange={(e) => setFormData({ ...formData, english_level: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seçiniz</option>
                      <option value="A1">A1</option>
                      <option value="A2">A2</option>
                      <option value="B1">B1</option>
                      <option value="B2">B2</option>
                      <option value="C1">C1</option>
                      <option value="C2">C2</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Öğrenci Tel
                    </label>
                    <input
                      type="text"
                      value={formData.student_phone}
                      onChange={(e) => setFormData({ ...formData, student_phone: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Öğrenci Mail
                    </label>
                    <input
                      type="email"
                      value={formData.student_email}
                      onChange={(e) => setFormData({ ...formData, student_email: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Veli İsim Soyisim
                    </label>
                    <input
                      type="text"
                      value={formData.guardian_name}
                      onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Veli Tel
                    </label>
                    <input
                      type="text"
                      value={formData.guardian_phone}
                      onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Veli Mail
                    </label>
                    <input
                      type="email"
                      value={formData.guardian_email}
                      onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Not
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Durumu
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Yeni">Yeni</option>
                      <option value="Arandı">Arandı</option>
                      <option value="Görüşme Yapıldı">Görüşme Yapıldı</option>
                      <option value="Lead Oldu">Lead Oldu</option>
                      <option value="İlgilenmedi">İlgilenmedi</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={closeModals}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <span>{showEditModal ? 'Güncelle' : 'Kaydet'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && selectedEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kaydı Sil</h3>
                  <p className="text-gray-600 dark:text-gray-400">Bu işlem geri alınamaz</p>
                </div>
              </div>

              <p className="text-gray-700 dark:text-gray-300 mb-6">
                <strong>{selectedEntry.student_name}</strong> adlı kaydı kalıcı olarak silmek istediğinize emin misiniz?
              </p>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSelectedEntry(null);
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={handleDelete}
                  className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Sil</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FairData;
